import { OnboardingProvider, useOnboarding } from "@onboardjs/react";
import { useMutation, useQuery } from "convex/react";
import { useCallback, useMemo } from "react";
import { Navigate, useNavigate } from "react-router";
import { api } from "../../../convex/_generated/api";
import { Skeleton } from "../../components/ui/skeleton";
import { setupComponentRegistry } from "./registry";
import { SetupShell } from "./SetupShell";
import { SETUP_STEPS } from "./steps";

function SetupFlowContent() {
	const { state, renderStep } = useOnboarding();

	if (!state?.currentStep) {
		return (
			<div className="flex flex-col gap-3 p-6">
				<Skeleton className="h-4 w-3/4" />
				<Skeleton className="h-4 w-full" />
				<Skeleton className="h-4 w-2/3" />
			</div>
		);
	}

	if (state?.isCompleted) {
		return null;
	}

	return <SetupShell>{renderStep()}</SetupShell>;
}

export default function SetupPage() {
	const navigate = useNavigate();
	const onboardingState = useQuery(api.codex.queries.getOnboardingState);
	const firmProfile = useQuery(api.codex.queries.getFirmProfile);
	const isStaff = useQuery(api.staff.isCurrentUserStaff);
	const saveState = useMutation(api.codex.mutations.saveOnboardingState);
	const completeOnboarding = useMutation(
		api.codex.mutations.completeOnboarding,
	);

	const isAdmin = isStaff === true;

	const initialContext = useMemo(
		() => ({
			flowData: {
				firmProfileExists: firmProfile !== null && firmProfile !== undefined,
				isAdmin,
			},
		}),
		[firmProfile, isAdmin],
	);

	const handleFlowComplete = useCallback(async () => {
		// Only admins mark the org-level onboarding as complete
		if (isAdmin) {
			await completeOnboarding({});
		}
		navigate("/workspace", { replace: true });
	}, [isAdmin, completeOnboarding, navigate]);

	const handleDataLoad = useCallback(async () => {
		if (!onboardingState) return null;
		return {
			flowData: JSON.parse(onboardingState.flowData),
			currentStepId: onboardingState.currentStepId,
		};
	}, [onboardingState]);

	const handleDataPersist = useCallback(
		async (
			context: { flowData: Record<string, unknown> },
			currentStepId: string | number | null,
		) => {
			const firstStep = isAdmin ? "domain-discovery" : "connect-email";
			await saveState({
				currentStepId: String(currentStepId ?? firstStep),
				flowData: JSON.stringify(context.flowData),
			});
		},
		[isAdmin, saveState],
	);

	// Wait for initial data to load
	if (
		onboardingState === undefined ||
		firmProfile === undefined ||
		isStaff === undefined
	) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="flex flex-col gap-3 p-6">
					<Skeleton className="h-4 w-48" />
					<Skeleton className="h-4 w-64" />
				</div>
			</div>
		);
	}

	// Non-staff users skip setup entirely
	if (!isStaff) {
		return <Navigate to="/workspace" replace />;
	}

	return (
		<OnboardingProvider
			steps={SETUP_STEPS}
			componentRegistry={setupComponentRegistry}
			initialContext={initialContext}
			customOnDataLoad={handleDataLoad}
			customOnDataPersist={handleDataPersist}
			onFlowComplete={handleFlowComplete}
		>
			<SetupFlowContent />
		</OnboardingProvider>
	);
}
